import React from "react";
import C18Anchor00 from "./C18Anchor00";
import { privs, getZoneInfo } from "./C18utils";
import { constant, globs } from "../../components/utils/utils";
import { getParamId2, wsTrans, getPearlUsed,
  initSensorIds, sensorIds, isSensorErrVal } from "../utils/utils";
import { uploadTotal } from "../../components/utils/ws";
import { dbVals } from "../../components/utils/http";
import { pInd } from '../../components/utils/paramIds';
import { pearlSensorIds } from '../../components/utils/landruConstants';
import C18Button00 from './C18Button00'
import history from "../../history"

var c2f = (c) => {
  if (c == null || isNaN(c)) return null;
  return c * 9 / 5 + 32;
};

var mpsToMph = (mps) => {
  if (mps == null || isNaN(mps)) return null;
  return mps * 2.237;
};

var degToCompass = (deg) => {
  if (deg == null || isNaN(deg)) return "";
  const dirs = [
    "N","NNE","NE","ENE","E","ESE","SE","SSE",
    "S","SSW","SW","WSW","W","WNW","NW","NNW",
  ];
  return dirs[Math.round(((+deg % 360) + 360) % 360 / 22.5) % 16];
};

var roundTo = (val, places) => {
  let mult = 10 ** places;
  return Math.round(val * mult) / mult;
};

var cleanSensorVal = (val, isGeneric) => {
  if (val == null || val === 'None' || val === '--') return null;
  let n = +val;
  if (isNaN(n)) return null;
  if (isSensorErrVal(n, isGeneric) || n === -1 || (n > 774 && n < 780)) return null;
  return n;
};

var SKIP_PREFIXES = ["ca", "MB", "ST"];
var SKIP_TYPES = ["zoS"];
var OUTSIDE_SENSORS = [
  { sensorKey: "e0ouT", typeKey: "ouT", pearlKey: "outTemp", displayIndex: 4 },
  { sensorKey: "e0ouH", typeKey: "ouH", pearlKey: "outHum", displayIndex: 5 },
  { sensorKey: "e0ouL", typeKey: "ouL", pearlKey: "outLight", displayIndex: 8 },
  { sensorKey: "e0oWs", typeKey: "oWs", pearlKey: "wndSpd", displayIndex: 9 },
];
var OUTSIDE_SENSOR_TYPES = new Set(OUTSIDE_SENSORS.map(o => o.typeKey));

var sensorTypeMap = {
  inT: { icon: "thermostat", tone: "orange" },
  ouT: { icon: "thermostat", tone: "blue" },
  fbT: { icon: "thermostat", tone: "orange" },
  bpT: { icon: "thermostat", tone: "orange" },
  cnT: { icon: "eco", tone: "green" },
  inH: { icon: "humidity_percentage", tone: "blue" },
  ouH: { icon: "humidity_percentage", tone: "blue" },
  inL: { icon: "light_mode", tone: "orange" },
  ouL: { icon: "light_mode", tone: "blue" },
  vpd: { icon: "dew_point", tone: "purple" },
  inC: { icon: "co2", tone: "gray" },
  oWs: { icon: "air", tone: "gray" },
  ran: { icon: "rainy", tone: "blue" },
  sno: { icon: "cloudy_snowing", tone: "blue" },
  dPr: { icon: "compress", tone: "gray" },
  bPr: { icon: "compress", tone: "gray" },
  dli: { icon: "wb_twilight", tone: "orange" },
};

var getSensorDisplay = (typeKey) => {
  if (sensorTypeMap[typeKey]) return sensorTypeMap[typeKey];
  let short = typeKey.substring(0, 2);
  let shortMap = {
    at: { icon: "thermostat", tone: "orange" },
    sm: { icon: "grass", tone: "green" },
    ec: { icon: "science", tone: "blue" },
    ph: { icon: "water_drop", tone: "blue" },
    gn: { icon: "sensors", tone: "gray" },
    vp: { icon: "speed", tone: "gray" },
    vo: { icon: "electric_bolt", tone: "gray" },
    tp: { icon: "thermostat", tone: "orange" },
  };
  return shortMap[short] || { icon: "sensors", tone: "gray" };
};

class C18HydroRootSystemStatus extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      todayUsage: 0,
      currentFlow: 0,
    };
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.subscribe_zoneUpdate = globs.events.subscribe(
      "newZoneData", this.onNewData
    );
    this.loadFlowData();
    this.flowInterval = setInterval(this.loadFlowData, 10000);
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.subscribe_zoneUpdate) {
      this.subscribe_zoneUpdate.remove();
    }
    if (this.flowInterval) {
      clearInterval(this.flowInterval);
    }
  }

  onNewData = () => {
    this.updateCurrentFlow();
    this.forceUpdate();
  };

  getIrrChannels = (zInd) => {
    let chTypePid = getParamId2(1900, "configuration_channels", "channelType");
    let chFlowPid = getParamId2(1900, "config_channels_configuration", "irrFlowRate");
    let chPosPid = getParamId2(1900, "snapshot_channels", "position");
    let channels = [];
    try {
      let pearlUsed = getPearlUsed(zInd);
      pearlUsed.forEach(p => {
        let ch = p.chInd;
        let chType = +(dbVals.z[zInd]?.[ch]?.[chTypePid] || 0);
        if (chType === 2 || chType === 6) {
          channels.push({
            ch,
            zInd,
            chFlow: +(dbVals.z[zInd]?.[ch]?.[chFlowPid] || 0),
            chPos: +(dbVals.z[zInd]?.[ch]?.[chPosPid] || 0),
          });
        }
      });
    } catch (e) {}
    return channels;
  };

  updateCurrentFlow = () => {
    let zi = getZoneInfo(this.props.zone);
    if (!zi) return;
    let channels = this.getIrrChannels(zi.siteZoneIndex);
    let currentFlow = 0;
    channels.forEach(ch => {
      if (ch.chPos) currentFlow += ch.chFlow;
    });
    if (this.mounted) {
      this.setState({ currentFlow: roundTo(currentFlow, 1) });
    }
  };

  loadFlowData = async () => {
    let zi = getZoneInfo(this.props.zone);
    if (!zi) return;
    let zInd = zi.siteZoneIndex;
    let channels = this.getIrrChannels(zInd);

    let currentFlow = 0;
    channels.forEach(ch => {
      if (ch.chPos) currentFlow += ch.chFlow;
    });

    let cycles = {};
    try {
      let res = await wsTrans("usa", {
        cmd: "cRest", uri: "/s/irrigation",
        method: "retrieve", sessionId: globs.userData.session.sessionId,
        body: {
          siteId: this.props.site,
          cmd: "getCycles",
          tz: (new Date()).getTimezoneOffset(),
        },
      });
      cycles = res.data || {};
    } catch (e) {}

    let todayUsage = 0;
    channels.forEach(ch => {
      let cyc = cycles[`${ch.zInd}-${ch.ch}`];
      if (cyc) {
        todayUsage += ch.chFlow * cyc.runTime / 60;
      }
    });

    if (this.mounted) {
      this.setState({
        currentFlow: roundTo(currentFlow, 1),
        todayUsage: roundTo(todayUsage, 1),
      });
    }
  };

  getCanopyTemp = (zInd) => {
    try {
      let tempSensorId = getParamId2(1900, "configuration_zone_settings", "Vapor Pressure Deficit Media Sensor");
      let tempOffsetId = getParamId2(1900, "pearl_config", "canopy_offset");
      let anInpBaseId = getParamId2(1900, "snapshots", "analogTemperature1");
      let inTempId = getParamId2(1900, "snapshots", "inTemperature");

      let tempSensor = +(dbVals.z[zInd]?.[255]?.[tempSensorId] || 0);
      let tempOffset = +(dbVals.z[zInd]?.[240]?.[tempOffsetId] || 0);

      if (tempSensor >= 5) {
        if (tempSensor > 5) return null;
        return +(dbVals.z[zInd]?.[240]?.[inTempId] || 0) + tempOffset;
      }
      return +(dbVals.z[zInd]?.[240]?.[+anInpBaseId + tempSensor] || 0);
    } catch (e) {
      return null;
    }
  };

  buildSensorItem = (s, zInd) => {
    let prefix = s.substring(0, 2);
    if (SKIP_PREFIXES.includes(prefix)) return null;
    let typeKey = (["e0","e1","e2","e3"].includes(prefix)) ? s.substring(2) : s;
    if (SKIP_TYPES.includes(typeKey)) return null;
    let isChan = (typeKey[0] === "c" && !isNaN(typeKey.substring(1, 3)));
    if (isChan) return null;

    let sid = sensorIds?.[s];
    if (!sid) return null;

    let val;
    if (typeKey === "cnT") {
      val = this.getCanopyTemp(zInd);
    } else {
      val = (dbVals.z[zInd]?.[sid.ch]?.[sid.id]);
    }

    // generic sensors: the 1000000 "no sensor" value is shown raw, not as "--"
    let cleaned = cleanSensorVal(val);
    let unitStr = "";
    try {
      unitStr = (typeof sid.unit === "function") ? sid.unit(zInd).t : (sid.unit || "");
    } catch (e) {}
    if (typeKey.startsWith("sm")) unitStr = "VWC %";

    let display = getSensorDisplay(typeKey);
    let name = (sid.name2 || sid.name || typeKey).replace("Temperature", "Temp").replace(/^In\s+/, "");

    return {
      key: `sens-${s}`,
      icon: display.icon,
      value: cleaned != null ? `${roundTo(cleaned, 1)}` : "--",
      unit: unitStr,
      label: name,
      tone: display.tone,
      sensorId: s,
      typeKey,
    };
  };

  getSensorMapping = (zInd, pearlKey) => {
    try {
      let tab = pInd[1900]?.pearl_sensor_config;
      if (!tab) return 0;
      let mappingId = getParamId2(1900, "pearl_sensor_config", "mapping");
      let entSize = tab[2];
      let sensorIndex = pearlSensorIds[pearlKey]?.i;
      if (sensorIndex == null) return 0;
      return +(dbVals.z[zInd]?.[240]?.[mappingId + sensorIndex * entSize] || 0);
    } catch (e) { return 0; }
  };

  getMetarItem = (typeKey, mtv, displayIndex) => {
    switch (typeKey) {
      case "ouT": {
        let tempF = c2f(mtv.temp_c);
        return { key: "metar-temp", icon: "thermostat", value: tempF != null ? `${Math.round(tempF)}` : "--", unit: "\u00B0F", label: "Temperature", tone: "orange", displayIndex, isMetar: true };
      }
      case "ouH": {
        let hum = mtv.hum_pct;
        return { key: "metar-hum", icon: "opacity", value: hum != null ? `${Math.round(hum)}` : "--", unit: "%", label: "Humidity", tone: "blue", displayIndex, isMetar: true };
      }
      case "ouL": {
        let light = mtv.light_ppfd;
        return { key: "metar-light", icon: "wb_sunny", value: light != null ? `${Math.round(light)}` : "--", unit: "ppfd", label: "Light", tone: "orange", displayIndex, isMetar: true };
      }
      case "oWs": {
        let windMph = mpsToMph(mtv.wndSpd_mps);
        let windDir = degToCompass(mtv.wndDir_deg);
        return { key: "metar-wind", icon: "air", value: windMph != null ? `${Math.round(windMph)}` : "--", unit: "mph", label: windDir ? `Wind ${windDir}` : "Wind", tone: "gray", displayIndex, isMetar: true };
      }
      default: return null;
    }
  };

  getOutsideSensorItems = (zInd) => {
    let site = globs.sitesInfo?.info?.find(s => s.siteId === this.props.site);
    let mtv = site?.metarVals || {};
    let items = [];

    OUTSIDE_SENSORS.forEach(os => {
      let mapping = this.getSensorMapping(zInd, os.pearlKey);
      if (mapping !== 0) {
        let item = this.buildSensorItem(os.sensorKey, zInd);
        if (item) {
          item.label = item.label.replace(/^Outside\s*/i, "");
          item.displayIndex = os.displayIndex;
          items.push(item);
        }
      } else {
        let item = this.getMetarItem(os.typeKey, mtv, os.displayIndex);
        if (item) items.push(item);
      }
    });

    return items;
  };

  getSensorItems = () => {
    let zi = getZoneInfo(this.props.zone);
    if (!zi) return [];
    let zInd = zi.siteZoneIndex;
    initSensorIds(zInd);

    let sensorOrder = zi.sensorOrder || [];
    let items = [];

    sensorOrder.forEach(s => {
      if (!s) return;
      let item = this.buildSensorItem(s, zInd);
      if (item) items.push(item);
    });

    return items;
  };

  getWeatherItems = () => {
    let site = globs.sitesInfo?.info?.find(s => s.siteId === this.props.site);
    let mtv = site?.metarVals || {};

    let rain = mtv.rain;
    let snow = mtv.snow;

    let st = this.state;

    let items = [
      {
        key: "usage",
        icon: "water",
        value: `${st.todayUsage}`,
        unit: "gal",
        label: "Today's Calculated Usage",
        tone: "blue",
        displayIndex: 0,
      },
      {
        key: "flow",
        icon: "speed",
        value: `${st.currentFlow}`,
        unit: "gpm",
        label: "Calculated Flow",
        tone: "blue",
        displayIndex: 1,
      },
    ];

    if (rain > 0) {
      items.push({
        key: "rain",
        icon: "rainy",
        value: "",
        unit: "",
        label: "Rain",
        tone: "blue",
        displayIndex: 2,
      });
    }

    if (snow > 0) {
      items.push({
        key: "snow",
        icon: "cloudy_snowing",
        value: "",
        unit: "",
        label: "Snow",
        tone: "blue",
        displayIndex: 3,
      });
    }

    return items;
  };

  getUploadColor = () => {
    if (uploadTotal > 15) {
      if (uploadTotal > 30) {
        return "#FF8888";
      }
      return "#FFCC88";
    }
    if (uploadTotal > 3) {
      return "#FFFF88";
    }
    return "#88FF88";
  };

  handleWrench = (wrenchCmd, e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (this.props.onWrench) {
      this.props.onWrench({ wrenchCmd, e });
    }
  };
  sensorClick = (e, sensorId) => {
    e.preventDefault();
    let url = `/usa/c18/sites/${this.props.site}/zones/${this.props.zone}/sensor/${sensorId}`;
    history.push(url);
  };

  wrenchClick = (val, e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (this.props.parms?.onChange) {
      this.props.parms.onChange(val);
      return;
    }
    this.handleWrench(val.wrenchCmd, e);
  };

  renderItem = (item) => {
    const inner = (
      <>
        <span
          translate="no"
          className={`material-symbols-outlined hr16-weather-icon hr16-weather-${item.tone}`}
        >
          {item.icon}
        </span>
        <div className="hr16-weather-stack">
          {(item.value || item.unit) && (
            <div className="hr16-weather-value">
              {item.value}
              {item.unit && (
                <span className="hr16-weather-unit">{item.unit}</span>
              )}
            </div>
          )}
          <div className="hr16-weather-label">
            {item.isMetar && (
              <span translate="no" className="material-symbols-outlined hr16-metar-globe">public</span>
            )}
            {item.sensorId
              ? <span translate="no">{item.label}</span>
              : item.label}
          </div>
        </div>
      </>
    );

    if (item.sensorId) {
      return (
        <C18Anchor00
          to=""
          key={item.key}
          className={`hr16-weather-item hr16-weather-item-link hr16-weather-item-${item.key}`}
          aria-label={item.label}
          onClick={e => this.sensorClick(e, item.sensorId)}
        >
          {inner}
        </C18Anchor00>
      );
    }

    return (
      <div className={`hr16-weather-item hr16-weather-item-${item.key}`} key={item.key}>
        {inner}
      </div>
    );
  };

  render() {
    const { gatewayType } = this.props;
    const zi = getZoneInfo(this.props.zone);
    const zInd = zi?.siteZoneIndex;
    if (zi) initSensorIds(zInd);
    const outsideSensorItems = zi ? this.getOutsideSensorItems(zInd) : [];
    const allSensorItems = this.getSensorItems();
    const sensorItems = allSensorItems.filter(item => !OUTSIDE_SENSOR_TYPES.has(item.typeKey));
    const weatherItems = [...this.getWeatherItems(), ...outsideSensorItems]
      .sort((a, b) => (a.displayIndex ?? 10) - (b.displayIndex ?? 10));

    let isSuper = false;
    if (
      privs("super", "", constant.AREA_PRIVS_READ) ||
      (globs.privsInfo.thirdPartyAccounts?.length > 1)
    ) {
      isSuper = true;
    }
    const showAdminButtons = isSuper && [800, 1900].includes(gatewayType);
    const parms = this.props.parms || {
      siteId: this.props.site,
      zoneId: this.props.zone,
      isHR16: true,
      gatewayType: gatewayType,
      ariaLabel: "sensor settings",
      onChange: (val) => this.handleWrench(val.wrenchCmd, val.e),
    };

    return (
      <div className="hr16-weather-bar" aria-label="HydroRoot weather bar" id="system-status-bar">
        <div className="hr16-status-rows">
          <div className="hr16-weather-items">
            {weatherItems.map(item => this.renderItem(item))}
          </div>
          {sensorItems.length > 0 && (
            <div className="hr16-weather-items hr16-sensor-row">
              {sensorItems.map(item => this.renderItem(item))}
            </div>
          )}
        </div>
        <div className="hr16-button-grid section-controls custom-section-controls">
          <C18Anchor00 id="/zone/zoneSensors/quickCharts" to="" className="quick-charts " aria-label="quick-charts"
            onClick={e => this.wrenchClick({ wrenchCmd: "charts" }, e)}>
            <span translate="no" className="material-icons-outlined">
              signal_cellular_alt
            </span>
          </C18Anchor00>
          {(parms.siteId || parms.zoneId) && !parms.isHR16 &&
            <C18Button00 type="button" translate="no" className={`phys-button material-icons`} aria-pressed="false"
              onClick={() => { this.listTileClick("phys-button") }}>preview</C18Button00>
          }
          <C18Anchor00 id="/zone/zoneSensors/sensorSettings" to=""
            className="settings-button material-icons-outlined flip"
            translate="no"
            aria-label={parms.ariaLabel}
            style={{ position: "relative", zIndex: 5 }}
            onClick={e => this.wrenchClick({ wrenchCmd: "wrench" }, e)}>settings</C18Anchor00>
          {(([800,1900].includes(parms.gatewayType)) && isSuper) &&
            <>
              <C18Anchor00 to="" className="settings-button material-icons-outlined flip" translate="no"
                aria-label={parms.ariaLabel}
                title="Force full upload of this Zone"
                style={{ backgroundColor: this.getUploadColor() }}
                onClick={e => parms.onChange({ e: e, wrenchCmd: "upload" }, e)}>
                upload</C18Anchor00>
              <C18Anchor00 to="" className="settings-button material-icons-outlined flip" translate="no"
                aria-label={parms.ariaLabel}
                title="Delete all zone data"
                style={{ backgroundColor: "#FFCCCC" }}
                onClick={e => parms.onChange({ e: e, wrenchCmd: "eraseDB" }, e)}>
                delete</C18Anchor00>
            </>
          }
        </div>
      </div>
    );
  }
}

export default C18HydroRootSystemStatus;
